import { getCognitoToken } from './authUtils';
import {
  getSelectedOrganizationForSuperAdmin,
  getUserRole,
  UserRoles,
} from './enums';
import { maybeHandleZendeskAuthFailureFromResponse } from './zendeskAuth';
import Pusher from 'pusher-js/with-encryption';

interface ChannelAuthorizationData {
  auth: string;
  channel_data?: string;
  shared_secret?: string;
}

export const createPusherAuthHandler = (endpoint: string) => {
  return async (
    params: { channelName: string; socketId: string },
    callback: (
      error: Error | null,
      authData: ChannelAuthorizationData | null,
    ) => void,
  ) => {
    try {
      const headers: Record<string, string> = {};

      if (IS_ZENDESK_BUILD) {
        // Zendesk build: cookie auth via credentials: 'include'; no bearer.
      } else {
        // Add Authorization header with aws-amplify idToken
        const bearerToken = await getCognitoToken();
        headers['Authorization'] = `Bearer ${bearerToken}`;

        // For superadmin users, add the X-ORG-ID header
        if (getUserRole() === UserRoles.ROLE_SUPER_ADMIN) {
          const selectedOrgId =
            getSelectedOrganizationForSuperAdmin()?.org_id || '';
          headers['X-ORG-ID'] = String(selectedOrgId);
        }
      }

      const response = await fetch(endpoint, {
        body: JSON.stringify({
          channel_name: params.channelName,
          socket_id: params.socketId,
        }),
        ...(IS_ZENDESK_BUILD ? { credentials: 'include' as const } : {}),
        headers: {
          'Content-Type': 'application/json',
          ...headers,
        },
        method: 'POST',
      });

      await maybeHandleZendeskAuthFailureFromResponse(response);

      if (!response.ok) {
        throw new Error(`Pusher authorization failed: ${response.status}`);
      }

      const authData = await response.json();
      callback(null, authData);
    } catch (error) {
      console.error('Error authorizing Pusher channel:', error);
      callback(error as Error, null);
    }
  };
};

export const initializePusher = (): Pusher => {
  if (!window.pusher) {
    const endpoint = `${API_URL}pusher/dashboard/auth`;

    window.pusher = new Pusher(PUSHER_KEY, {
      channelAuthorization: {
        customHandler: createPusherAuthHandler(endpoint),
        endpoint,
        transport: 'ajax',
      },
      cluster: PUSHER_CLUSTER,
      // Always use TLS
      forceTLS: true,
    });
  }
  return window.pusher;
};
